import { NavLink, Outlet, useLocation } from "react-router";
import { useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "ghost" | "danger";
const variants: Record<Variant, string> = {
  // Ink with a soft top light and a lifted shadow: the one filled control on a page.
  primary: "bg-[var(--chrome)] bg-[linear-gradient(180deg,rgb(255_255_255/0.12),transparent_55%)] text-white shadow-[inset_0_0.5px_0_rgb(255_255_255/0.25),0_1px_2px_rgb(20_20_20/0.2),0_4px_12px_-4px_rgb(20_20_20/0.3)] hover:brightness-[1.18]",
  // Its own text colour: inside a dark bar (e.g. the Files selection bar) it would otherwise inherit white.
  secondary: "bg-[var(--panel)] text-[var(--text)] shadow-[0_0_0_1px_var(--line),0_1px_2px_rgb(20_20_20/0.05)] hover:bg-[#fafaf8] hover:shadow-[0_0_0_1px_var(--line-strong),0_2px_6px_-2px_rgb(20_20_20/0.1)]",
  ghost: "hover:bg-[rgb(20_20_20/0.05)] text-[var(--muted)] hover:text-[var(--text)]",
  danger: "bg-[var(--panel)] text-red-700 shadow-[0_0_0_1px_rgb(220_38_38/0.22),0_1px_2px_rgb(20_20_20/0.05)] hover:bg-red-50",
};

export function Button({ variant = "secondary", className, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...p}
      className={cx(
        "inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 text-[13.5px] font-medium tracking-[-0.005em] transition-[background-color,box-shadow,filter,transform] duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45",
        variants[variant],
        className,
      )}
    />
  );
}

const field = "h-9 min-w-0 rounded-[11px] border-0 bg-[var(--panel)] px-3.5 text-sm shadow-[0_0_0_1px_var(--line),inset_0_1px_2px_rgb(20_20_20/0.03)] outline-none transition-shadow placeholder:text-[var(--muted)]/75 hover:shadow-[0_0_0_1px_var(--line-strong),inset_0_1px_2px_rgb(20_20_20/0.03)] focus:shadow-[0_0_0_1px_var(--color-accent),0_0_0_4px_color-mix(in_srgb,var(--color-brand)_22%,transparent)] focus-visible:outline-none";
/** Full width unless the caller sets one (two width classes on one element: the stylesheet's order would pick, not the caller). */
const width = (className?: string) => !/(^|\s)w-/.test(className ?? "") && "w-full";

export function Input({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...p} className={cx(field, width(className), className)} />;
}

export function Select({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...p} className={cx(field, width(className), "select-chevron truncate", className)} />;
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[13px] font-medium">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[var(--muted)]">{hint}</span>}
    </label>
  );
}

export function Switch({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start justify-between gap-4 rounded-lg py-2 text-left disabled:opacity-50"
    >
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-[var(--muted)]">{hint}</span>}
      </span>
      <span className={cx("relative mt-0.5 h-[22px] w-[38px] shrink-0 rounded-full shadow-[inset_0_1px_2px_rgb(20_20_20/0.12)] transition-colors duration-200", checked ? "bg-[var(--color-accent)]" : "bg-[#e3e1db]")}>
        <span className={cx("absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_3px_rgb(20_20_20/0.25),0_0_0_0.5px_rgb(20_20_20/0.06)] transition-[left] duration-200 ease-[cubic-bezier(0.3,1.4,0.5,1)]", checked ? "left-[18px]" : "left-[2px]")} />
      </span>
    </button>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("min-w-0 rounded-2xl border border-[var(--line)] bg-[var(--panel)] shadow-[var(--shadow)]", className)}>{children}</div>;
}

const tones = {
  neutral: "bg-[var(--panel-2)] text-[var(--muted)] ring-[var(--line)]",
  accent: "bg-[var(--color-brand)]/12 text-[var(--color-accent)] ring-[var(--color-brand)]/25",
  good: "bg-emerald-500/10 text-emerald-700 ring-emerald-600/15",
  bad: "bg-red-500/10 text-red-700 ring-red-600/15",
};
export function Badge({ tone = "neutral", children }: { tone?: keyof typeof tones; children: ReactNode }) {
  return <span className={cx("inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ring-inset", tones[tone])}>{children}</span>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0 max-w-2xl">
        <h1 className="text-[26px] leading-tight font-semibold">{title}</h1>
        {description && <p className="mt-1.5 text-[14.5px] leading-relaxed text-[var(--muted)]">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Native <dialog> — focus trap, Esc and backdrop for free. */
export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean | "xl" }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className={cx(
        "m-auto w-[calc(100%-2rem)] rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-0 text-[var(--text)] shadow-[var(--shadow-lg)] backdrop:bg-[#141414]/45 backdrop:backdrop-blur-[3px]",
        wide === "xl" ? "max-w-4xl" : wide ? "max-w-2xl" : "max-w-md",
      )}
    >
      {open && (
        <div className="max-h-[85vh] overflow-y-auto p-6">
          <h2 className="mb-5 font-display text-xl font-semibold">{title}</h2>
          {children}
        </div>
      )}
    </dialog>
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="rounded-[10px] border border-red-600/15 bg-red-500/[0.07] px-3 py-2 text-sm text-red-700">{error instanceof Error ? error.message : String(error)}</p>;
}

export function Spinner() {
  return <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--line)] border-t-[var(--color-accent)]" />;
}

/** For a row of links that scrolls sideways (menus and tabs on phones): keeps the current page's link in view. */
export function useActiveInView<T extends HTMLElement>(key: unknown) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const row = ref.current;
    const a = row?.querySelector<HTMLElement>('a[aria-current="page"]');
    if (!row || !a || row.scrollWidth <= row.clientWidth) return;
    row.scrollLeft += a.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - a.offsetWidth) / 2;
  }, [key]);
  return ref;
}

/** A page split into tabs; each tab is its own route (so refresh, back and links keep the tab). */
export function TabbedLayout({ tabs }: { tabs: { to: string; label: string }[] }) {
  const ref = useActiveInView<HTMLElement>(useLocation().pathname);
  return (
    <>
      <nav ref={ref} aria-label="Sections" className="mb-8 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="inline-flex gap-0.5 rounded-full bg-[rgb(20_20_20/0.05)] p-1">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                cx(
                  "shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-[background-color,color,box-shadow] duration-150",
                  isActive ? "bg-[var(--panel)] text-[var(--text)] shadow-[0_1px_2px_rgb(20_20_20/0.08),0_0_0_0.5px_rgb(20_20_20/0.06)]" : "text-[var(--muted)] hover:text-[var(--text)]",
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <Outlet />
    </>
  );
}

/** The pill track behind a small choice of views, and one choice in it (shared by the page tabs' look). */
export const segTrack = "inline-flex shrink-0 gap-0.5 rounded-full bg-[rgb(20_20_20/0.05)] p-[3px]";
export const segItem = (on: boolean) =>
  cx(
    "whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-[background-color,color,box-shadow] duration-150",
    on ? "bg-[var(--panel)] text-[var(--text)] shadow-[0_1px_2px_rgb(20_20_20/0.08),0_0_0_0.5px_rgb(20_20_20/0.06)]" : "text-[var(--muted)] hover:text-[var(--text)]",
  );

/** A small choice of views (e.g. a chart's measure). */
export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className={segTrack}>
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} className={segItem(value === o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "26–50 of 130", Previous / Next and a page-size choice, for lists paged on the server. */
export function Pagination({ page, pageSize, total, onPage, onPageSize, busy }: { page: number; pageSize: number; total: number; onPage: (p: number) => void; onPageSize?: (n: number) => void; busy?: boolean }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= 0 || (total <= pageSize && !onPageSize)) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  // The list is above this bar: after turning a page, bring its top back into view.
  const go = (p: number, e: React.MouseEvent<HTMLElement>) => {
    const card = e.currentTarget.closest("[data-pager]")?.parentElement;
    onPage(p);
    card?.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  return (
    <div data-pager className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-[var(--line)] px-4 py-3 text-sm text-[var(--muted)]">
      <span className={cx(busy && "opacity-60")}>
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {onPageSize && (
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} aria-label="Rows per page" className="select-chevron h-8 rounded-full bg-[var(--panel)] pl-3 text-xs shadow-[0_0_0_1px_var(--line)]">
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        )}
        <Button className="h-8" disabled={page <= 1} onClick={(e) => go(page - 1, e)}>Previous</Button>
        <span className="hidden min-w-20 text-center text-xs sm:inline">Page {page} of {pages.toLocaleString()}</span>
        <Button className="h-8" disabled={page >= pages} onClick={(e) => go(page + 1, e)}>Next</Button>
      </div>
    </div>
  );
}
